/**
 * "Follow one person": a stepper through `lib/journey.tsx`, ported from the
 * concept's `PersonJourney.jsx` and its `.bp-pj*` / `.bp-thread` / `.bp-msg`
 * styles into Tailwind on the product's tokens.
 *
 * ## CONTROLLED, SO THE LOOP CAN FOLLOW IT
 *
 * The section owns `step` (see `sections/Loop.tsx`): the loop above lights the
 * part and the edge each step moves data along, and the loop's "See it in
 * Priya’s story" sets the step and focuses this component's heading, which is
 * why the heading takes a ref and `tabIndex={-1}`.
 *
 * ## KEYBOARD
 *
 * Prev and Next are buttons; each dot is a button named "Step 4: Connect" with
 * `aria-current="step"` on the current one; and ← / → (plus Home / End) step
 * from anywhere inside the stepper — the handler is on the wrapper, so it works
 * whichever control has focus. The key logic is `stepForKey`, a pure function
 * the site suite tests. The current step is announced through a polite status
 * line, because the body changes under the reader's focus.
 *
 * Prev and Next use `aria-disabled` rather than `disabled` at the ends, as the
 * concept did: a disabled button drops focus to the page, and a keyboard
 * reader who pressed Next on the last step would lose their place.
 *
 * ## THE TWO COLOURS THAT ARE NOT THE BRAND'S
 *
 * The WhatsApp thread is drawn green for the customer's own messages, as
 * WhatsApp draws them, with `good` at 16% — the concept's own values. A tapped
 * quick-reply is outlined in `good`. Nothing else here uses a status colour; a
 * Sense finding is `warn`-tinted, as the product's Today page draws one.
 */
import type { KeyboardEvent, RefObject } from 'react';

import { cn } from '@wend/ui';

import { JOURNEY, JOURNEY_PERSON, stepForKey, type ThreadMessage } from '../lib/journey';
import { PARTS, PART_ORDER } from '../lib/parts';

const navButton = cn(
  'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[0.85rem] transition-[background-color,opacity]',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b aria-disabled:cursor-default aria-disabled:opacity-35',
);

function Thread({ items, business }: { items: readonly ThreadMessage[]; business: string }) {
  return (
    <div
      role="group"
      aria-label={`WhatsApp thread, as ${JOURNEY_PERSON.name} sees it`}
      className="max-w-[430px] overflow-hidden rounded-[14px] border border-line-strong bg-ink bg-dot-grid pb-3"
    >
      <div className="mb-2.5 flex items-center gap-2.5 border-b border-line bg-white/5 px-3 py-2 text-[0.82rem]">
        <span aria-hidden="true" className="grid size-7 flex-none place-items-center rounded-full bg-brand-gradient text-[0.74rem] font-semibold text-white">
          {business.charAt(0)}
        </span>
        <span>
          <strong className="block text-[0.84rem] text-text">{business}</strong>
          <span className="block text-[0.68rem] text-muted">business account · WhatsApp</span>
        </span>
      </div>
      {items.map((message, index) => (
        <div key={index} className={cn('mt-2 flex flex-col px-3', message.who === 'her' ? 'items-end' : 'items-start')}>
          <div
            className={cn(
              'relative min-w-[88px] max-w-[86%] rounded-[12px] px-[11px] pb-[18px] pt-2 text-[0.84rem] leading-[1.45]',
              message.who === 'her'
                ? 'rounded-tr-[4px] border border-good/40 bg-good/16 text-text'
                : 'rounded-tl-[4px] border border-line-strong bg-surface-3 text-text',
            )}
          >
            <span className="sr-only">{message.who === 'her' ? `${JOURNEY_PERSON.name}: ` : `${business}: `}</span>
            {message.text}
            <span className="absolute bottom-1 right-[9px] font-mono text-[0.58rem] text-muted">{message.time}</span>
          </div>
          {message.buttons !== undefined && (
            <div className="mt-1 grid w-[min(86%,320px)] gap-1">
              {message.buttons.map((label) => (
                <span
                  key={label}
                  className={cn(
                    'rounded-[9px] border bg-surface-3 px-2.5 py-1.5 text-center text-[0.8rem]',
                    message.tapped === label ? 'border-good/55 text-good' : 'border-line text-brand-ink',
                  )}
                >
                  {label}
                  {message.tapped === label && <span className="font-mono text-[0.62rem]"> · tapped</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function PersonJourney({
  step,
  onStep,
  titleRef,
}: {
  step: number;
  onStep: (next: number) => void;
  titleRef?: RefObject<HTMLHeadingElement | null>;
}) {
  const current = JOURNEY[step] ?? JOURNEY[0];
  if (current === undefined) return null;
  const part = PARTS[current.part];
  const seen = new Set(JOURNEY.slice(0, step + 1).map((entry) => entry.part));
  const last = JOURNEY.length - 1;

  const go = (next: number): void => onStep(Math.max(0, Math.min(last, next)));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Typing in a field is never hijacked (there is none here today; this keeps it so).
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
    const next = stepForKey(step, event.key);
    if (next === null) return;
    event.preventDefault();
    go(next);
  };

  return (
    <div onKeyDown={onKeyDown} className="overflow-hidden rounded-lg border border-line-strong bg-surface-1 shadow-lift">
      <div className="flex flex-wrap items-center justify-between gap-3.5 border-b border-line bg-white/[0.035] px-[18px] py-3.5">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="grid size-[38px] flex-none place-items-center rounded-full bg-linear-135 from-[#F2A65A] to-[#E0607E] text-[0.8rem] font-semibold text-white">
            {JOURNEY_PERSON.initials}
          </span>
          <div>
            <p className="text-[0.92rem] text-muted">
              <strong className="text-text">{JOURNEY_PERSON.name}</strong>
            </p>
            <p className="text-[0.76rem] text-muted">{JOURNEY_PERSON.meta}</p>
          </div>
        </div>
        <ol aria-label="Parts this story has reached" className="flex flex-wrap gap-1.5">
          {PART_ORDER.map((id) => {
            const on = id === current.part;
            return (
              <li
                key={id}
                aria-current={on ? 'step' : undefined}
                className={cn(
                  'rounded-full border px-2.5 py-1 font-mono text-[0.66rem] tracking-[0.03em] transition-colors',
                  on
                    ? 'border-transparent bg-brand-gradient text-white shadow-brand'
                    : seen.has(id)
                      ? 'border-line-strong bg-white/[0.04] text-text'
                      : 'border-line text-muted',
                )}
              >
                <span aria-hidden="true">{PARTS[id].glyph}</span> {PARTS[id].name}
              </li>
            );
          })}
        </ol>
      </div>

      <p role="status" className="sr-only">
        Step {step + 1} of {JOURNEY.length} · {part.name} · {current.title}
      </p>

      <div className="grid gap-[22px] p-[22px] min-[880px]:min-h-[340px] min-[880px]:grid-cols-[1.4fr_1fr] min-[880px]:p-[26px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[0.68rem]">
            <span className="uppercase tracking-[0.1em] text-brand-ink">
              Step {step + 1} of {JOURNEY.length}
            </span>
            <span className="rounded-full border border-brand-b/45 bg-brand-a/22 px-2.5 py-0.5 text-brand-ink-strong">
              <span aria-hidden="true">{part.glyph}</span> {part.name}
            </span>
            <span className="text-muted">{current.when}</span>
          </div>
          <h4
            ref={titleRef}
            tabIndex={-1}
            className="mb-2 mt-3 rounded-sm text-[clamp(1.12rem,1.9vw,1.36rem)] font-semibold tracking-[-0.018em] text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b"
          >
            {current.title}
          </h4>
          <p className="mb-3 text-[0.94rem] leading-[1.65] text-muted [&_code]:font-mono [&_code]:text-[0.85em] [&_code]:text-brand-ink [&_strong]:font-semibold [&_strong]:text-text">
            {current.body}
          </p>
          {current.note !== undefined && (
            <p className="mb-3 border-l-2 border-line-strong pl-2.5 text-[0.8rem] italic text-muted">{current.note}</p>
          )}
          {current.rules !== undefined && (
            <div className="mb-3 mt-1 flex flex-wrap items-center gap-1.5">
              {current.rules.map((rule, index) => (
                <span key={rule} className="inline-flex items-center gap-1.5">
                  {index > 0 && <span className="font-mono text-[0.62rem] tracking-[0.12em] text-brand-ink">AND</span>}
                  <span className="rounded-[7px] border border-brand-b/35 bg-brand-a/18 px-2 py-0.5 text-[0.78rem] text-brand-ink">{rule}</span>
                  <span aria-hidden="true" className="text-[0.8rem] text-good">
                    ✓
                  </span>
                  <span className="sr-only">matched</span>
                </span>
              ))}
            </div>
          )}
          {current.finding !== undefined && (
            <div
              className={cn(
                'mb-3 mt-1 rounded-base border px-3.5 py-3',
                current.finding.early ? 'border-line-strong bg-white/[0.035]' : 'border-warn/36 bg-warn/7',
              )}
            >
              <p className={cn('font-mono text-[0.62rem] uppercase tracking-[0.12em]', current.finding.early ? 'text-muted' : 'text-warn')}>
                <span aria-hidden="true">◆ </span>
                {current.finding.kicker}
              </p>
              <p className="mt-0.5 text-[0.92rem] font-semibold text-text">{current.finding.title}</p>
              <p className="mt-1 font-mono text-[0.72rem] text-muted">{current.finding.detail}</p>
            </div>
          )}
          {current.thread !== undefined && <Thread items={current.thread} business={JOURNEY_PERSON.business} />}
        </div>

        <aside aria-label="Data that moved at this step" className="self-start rounded-base border border-line-strong bg-black/20 p-4">
          <p className="font-mono text-[0.64rem] uppercase tracking-[0.13em] text-muted">Data that moved</p>
          <p className="mt-2.5 flex flex-wrap items-center gap-2 text-[0.9rem] font-semibold text-text">
            <span>{current.moved.from}</span>
            <span aria-hidden="true" className="text-brand-b">
              →
            </span>
            <span className="sr-only">to</span>
            <span>{current.moved.to}</span>
          </p>
          <p className="mt-2 inline-block rounded-[6px] border border-brand-b/35 bg-brand-a/18 px-2 py-0.5 font-mono text-[0.72rem] text-brand-ink">
            {current.moved.what}
          </p>
          <dl className="mt-3 font-mono text-[0.74rem]">
            {current.moved.rows.map(([key, value]) => (
              <div key={key} className="grid grid-cols-[minmax(96px,auto)_1fr] gap-2.5 border-t border-dashed border-line py-1.5">
                <dt className="text-muted">{key}</dt>
                <dd className="break-words text-text">{value}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-white/[0.025] px-[18px] py-3 min-[561px]:flex-nowrap">
        <button
          type="button"
          aria-label="Previous step"
          aria-disabled={step === 0 || undefined}
          onClick={() => go(step - 1)}
          className={cn(navButton, 'flex-1 justify-center border-line-strong bg-white/5 text-text hover:bg-white/10 min-[561px]:flex-none')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Prev
        </button>
        <div role="group" aria-label="Jump to a step" className="order-first flex w-full flex-wrap justify-center gap-1.5 min-[561px]:order-none min-[561px]:w-auto">
          {JOURNEY.map((entry, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Step ${index + 1}: ${PARTS[entry.part].name}`}
              aria-current={index === step ? 'step' : undefined}
              onClick={() => go(index)}
              className={cn(
                'grid size-7 place-items-center rounded-full border p-0 text-[0.7rem] transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
                index === step
                  ? 'border-transparent bg-brand-gradient text-white shadow-[0_0_0_3px_rgba(155,94,246,0.25)]'
                  : index < step
                    ? 'border-brand-b/35 bg-brand-a/16 text-brand-ink hover:text-white'
                    : 'border-line-strong text-muted hover:border-brand-b/60 hover:text-white',
              )}
            >
              <span aria-hidden="true">{PARTS[entry.part].glyph}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label="Next step"
          aria-disabled={step === last || undefined}
          onClick={() => go(step + 1)}
          className={cn(navButton, 'flex-1 justify-center border-transparent bg-brand-gradient text-white shadow-brand hover:brightness-110 min-[561px]:flex-none')}
        >
          Next
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
